import { useAsObservableSource, useLocalStore } from 'mobx-react-lite';
import { useEffect, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';

import { t } from '@gem/modules/i18n';
import { replaceHtmlTagsToSpecialSymbols } from '@gem/utils';

import Tasks from 'APP/Tasks';
import { PayloadType } from 'APP/model/message/messageModel.types';
import { RouterPage } from 'APP/router/constants';
import { useNavigateTo } from 'APP/router/hooks/useNavigateTo';
import Entities from 'STORE';

import { createArticle } from '../Tasks/createArticle';
import { createDraftArticle } from '../Tasks/createDraftArticle';
import { getArticlePayloadClass } from '../Tasks/getArticlePayloadClass';
import { sendDraftArticle } from '../Tasks/sendDraftArticle';
import { updateArticle } from '../Tasks/updateArticle';
import { updateDraftArticle } from '../Tasks/updateDraftArticle';
import getActualSortedOrder from '../services/payloads/getActualSortedOrder';

const SUPPORTED_TYPES = [
  PayloadType.Video,
  PayloadType.File,
  PayloadType.Image,
  PayloadType.AudioMessage,
  PayloadType.Location,
  PayloadType.RichText,
];

export default ({ payloads, scroll, groupId }) => {
  const params = useParams();
  const navigateTo = useNavigateTo();
  const source = useAsObservableSource({
    payloads,
    scroll,
    groupId,
    params,
  });

  const presenter = useLocalStore(() => ({
    isEditMode: false,
    draft: null,

    initEditMode: async () => {
      presenter.isEditMode = true;
      if (!presenter.message) {
        presenter.draft = await Tasks.drafts.getDraft({
          groupId: source.groupId,
          draftId: source.params.messageId,
        });
        //filtered unsupported payloads
        if (presenter.draft) {
          presenter.draft.payload.payloads = getActualSortedOrder(
            presenter.draft.payload.payloads.filter((x) =>
              SUPPORTED_TYPES.includes(x.payload.payloadType)
            )
          );
        }
      }

      source.payloads.loadData(presenter.message);
    },

    handleCreateArticle: async () => {
      if (presenter.draft) {
        const data = presenter.handleSendDraftData;
        await sendDraftArticle(
          data.groupId,
          data.id,
          data.clientUuid,
          getArticlePayloadClass(data.payload)
        );

        return;
      }

      const data = presenter.handleCreateArticleData;
      await createArticle(data.groupId, getArticlePayloadClass(data.payload));
    },

    handleUpdateArticle: async () => {
      const data = presenter.handleUpdateArticleData;
      await updateArticle(data.groupId, data.messageId, getArticlePayloadClass(data));
    },

    handleCreateDraft: async () => {
      const data = presenter.handleCreateDraftData;
      await createDraftArticle(data.groupId, getArticlePayloadClass(data.payload));
    },

    handleUpdateDraft: async () => {
      const data = presenter.handleUpdateDraftData;
      await updateDraftArticle(data.groupId, data.draftId, getArticlePayloadClass(data.payload));
    },

    get group() {
      return Entities.GroupsStore.getGroupById(source.groupId);
    },

    get message() {
      if (!presenter.group) {
        return null;
      }

      return (
        presenter.draft ||
        presenter.group.messagesStore.getMessageById(source.params.messageId) ||
        null
      );
    },

    get messages() {
      return presenter.group.messagesStore.messages.length;
    },

    get payloads() {
      return source.payloads;
    },

    get scroll() {
      return source.scroll;
    },

    get isDisabled() {
      if (
        presenter.draft &&
        presenter.payloads.isTitleValid &&
        presenter.payloads.somethingTextPayload
      ) {
        return false;
      }

      if (!presenter.payloads.isTitleValid) {
        return true;
      }

      if (presenter.hasPayloadSomethingLoading) {
        return true;
      }

      if (!presenter.payloads.somethingTextPayload) {
        return true;
      }

      if (presenter.isEditMode && !presenter.payloads.isChanged) {
        return true;
      }

      return !replaceHtmlTagsToSpecialSymbols(presenter.payloads.title).trim().length;
    },

    get isLoading() {
      if (!presenter.isEditMode) {
        return true;
      }

      return presenter.message;
    },

    get hasPayloadSomethingLoading() {
      return presenter.payloads.somethingLoading.size;
    },

    get hasSomethingData() {
      return (
        presenter.payloads.title.trim().length ||
        presenter.payloads.somethingTextPayload ||
        presenter.payloads.data.length
      );
    },

    get handleCreateArticleData() {
      return {
        groupId: presenter.group.id,
        payload: {
          title: replaceHtmlTagsToSpecialSymbols(presenter.payloads.title.trim()),
          payloads: presenter.payloads.getFinalPayloads({
            isEditMode: presenter.isEditMode,
            isDraft: presenter.draft,
          }),
        },
      };
    },

    get handleSendDraftData() {
      return {
        id: presenter.draft.id,
        groupId: presenter.draft.groupId,
        clientUuid: presenter.draft.clientUuid,
        payload: {
          title: replaceHtmlTagsToSpecialSymbols(presenter.payloads.title.trim()),
          payloads: presenter.payloads.getFinalPayloads({
            isEditMode: presenter.isEditMode,
            isDraft: presenter.draft,
          }),
        },
      };
    },

    get handleUpdateArticleData() {
      return {
        groupId: presenter.group.id,
        messageId: source.params.messageId,
        title: replaceHtmlTagsToSpecialSymbols(presenter.payloads.title.trim()),
        payloads: presenter.payloads.getFinalPayloads({
          isEditMode: presenter.isEditMode,
        }),
      };
    },

    get handleCreateDraftData() {
      return {
        groupId: presenter.group.id,
        payload: {
          title: replaceHtmlTagsToSpecialSymbols(presenter.payloads.title.trim()),
          payloads: presenter.payloads.getFinalPayloads({ isEditMode: false }),
        },
      };
    },

    get handleUpdateDraftData() {
      return {
        groupId: presenter.draft.groupId,
        draftId: presenter.draft.id,
        payload: {
          title: replaceHtmlTagsToSpecialSymbols(presenter.payloads.title.trim()),
          payloads: presenter.payloads.getFinalPayloads({
            isEditMode: presenter.isEditMode,
          }),
        },
      };
    },
  }));

  useLayoutEffect(() => {
    if (params.messageId) {
      presenter.initEditMode();
    }
  }, [params.messageId, presenter.message]);

  useEffect(() => {
    if (presenter.group.isSchedule && presenter.isEditMode && !presenter.message) {
      Tasks.app.showAlert(t('channel_schedule_editing_alert'));

      navigateTo({
        to: `/${RouterPage.Channels}/${presenter.group.groupId}`,
      });
    }
  }, [presenter.message, presenter.group, presenter.isEditMode]);

  return presenter;
};
